import { TicketLabel } from 'shared/types/helpdesk';

export interface AnalyticsQuery {
  type: 'label-frequency' | 'by-building' | 'by-unit' | 'by-reporter' | 'time-series' | 'filtered-list' | 'unknown';
  filters: {
    buildingId?: string;
    unitId?: string;
    label?: string;
    startDate?: Date;
    endDate?: Date;
    includeClosedTickets?: boolean; // Default: false (only open tickets)
  };
  granularity?: 'day' | 'week' | 'month';
  chartType?: 'bar' | 'line' | 'pie' | 'table';
  title: string;
}

/**
 * Natuurlijke taal parser voor analytics queries
 *
 * Parseert vragen zoals:
 * - "Hoeveel tickets ivm vochtschade heb ik?"
 * - "Hoe evolueren tickets voor EMIE-12 in de tijd?"
 * - "Waar heb ik de meeste elektriciteitsproblemen?"
 * - "Tickets per gebouw deze maand"
 * - "Vochtschade in 2000-EMIE-12"
 */

/**
 * Vertaal Nederlandse label keywords naar TicketLabel enums
 */
const labelKeywords: Record<string, TicketLabel[]> = {
  // Buitenschrijnwerk - exterior joinery (windows, exterior doors)
  'raam': [TicketLabel.Buitenschrijnwerk],
  'ramen': [TicketLabel.Buitenschrijnwerk],
  'venster': [TicketLabel.Buitenschrijnwerk],
  'glas': [TicketLabel.Buitenschrijnwerk],
  'voordeur': [TicketLabel.Buitenschrijnwerk],
  'buitendeur': [TicketLabel.Buitenschrijnwerk],

  // Glasbreuk - glass breakage
  'glasbreuk': [TicketLabel.Glasbreuk],
  'gebroken glas': [TicketLabel.Glasbreuk],

  // Binnenschrijnwerk - interior joinery (interior doors)
  'deur': [TicketLabel.Binnenschrijnwerk],
  'deuren': [TicketLabel.Binnenschrijnwerk],
  'binnendeur': [TicketLabel.Binnenschrijnwerk],

  // Vocht/water - moisture and water damage
  'vocht': [TicketLabel.VochtWater],
  'vochtschade': [TicketLabel.VochtWater],
  'water': [TicketLabel.VochtWater],
  'lek': [TicketLabel.VochtWater],
  'lekkage': [TicketLabel.VochtWater],
  'nat': [TicketLabel.VochtWater],
  'schimmel': [TicketLabel.VochtWater],

  // Verwarming - heating
  'verwarming': [TicketLabel.Verwarming],
  'cv': [TicketLabel.Verwarming],
  'radiator': [TicketLabel.Verwarming],
  'koud': [TicketLabel.Verwarming],
  'warm': [TicketLabel.Verwarming],

  // Elektriciteit - electricity (with common typo variations)
  'elektriciteit': [TicketLabel.Elektriciteit],
  'elektriciteits': [TicketLabel.Elektriciteit],
  'elektriciteitsproblemen': [TicketLabel.Elektriciteit],
  'elekticiteitsproblemen': [TicketLabel.Elektriciteit], // common typo: missing 'r'
  'elektrisch': [TicketLabel.Elektriciteit],
  'electrisch': [TicketLabel.Elektriciteit], // alternative spelling
  'stroom': [TicketLabel.Elektriciteit],
  'stopcontact': [TicketLabel.Elektriciteit],
  'schakelaar': [TicketLabel.Elektriciteit],

  // Sanitair - plumbing
  'sanitair': [TicketLabel.Sanitair],
  'toilet': [TicketLabel.Sanitair],
  'wc': [TicketLabel.Sanitair],
  'kraan': [TicketLabel.Sanitair],
  'douche': [TicketLabel.Sanitair],
  'verstopt': [TicketLabel.Sanitair, TicketLabel.Verstopping],
  'verstopping': [TicketLabel.Verstopping],

  // Verlichting - lighting
  'verlichting': [TicketLabel.Verlichting],
  'lamp': [TicketLabel.Verlichting],
  'licht': [TicketLabel.Verlichting],
  'tl': [TicketLabel.Verlichting],
  'led': [TicketLabel.Verlichting],

  // Ventilatie - ventilation
  'ventilatie': [TicketLabel.Ventilatie],
  'afzuiging': [TicketLabel.Ventilatie],
  'lucht': [TicketLabel.Ventilatie],
  'rooster': [TicketLabel.Ventilatie],

  // Sloten- en toegangsbeheer - locks and access control
  'slot': [TicketLabel.Toegangsbeheer],
  'sloten': [TicketLabel.Toegangsbeheer],
  'deurslot': [TicketLabel.Toegangsbeheer],
  'cilinder': [TicketLabel.Toegangsbeheer],
  'toegang': [TicketLabel.Toegangsbeheer],

  // Lift - elevator
  'lift': [TicketLabel.Lift],
  'elevator': [TicketLabel.Lift],

  // Dak - roof
  'dak': [TicketLabel.Dak],
  'dakgoot': [TicketLabel.Dak],
  'goot': [TicketLabel.Dak],

  // Riolering - sewage
  'riool': [TicketLabel.Riolering],
  'riolering': [TicketLabel.Riolering],
  'afvoer': [TicketLabel.Riolering],
  'geur': [TicketLabel.Riolering],

  // Onderhoud - maintenance
  'onderhoud': [TicketLabel.Onderhoud],
  'controle': [TicketLabel.Onderhoud],

  // Poetsen - cleaning
  'opkuis': [TicketLabel.Poetsen],
  'poetsen': [TicketLabel.Poetsen],
  'schoonmaak': [TicketLabel.Poetsen],
  'vuil': [TicketLabel.Poetsen],

  // Afval & Ongedierte - waste and pests
  'ongedierte': [TicketLabel.AfvalEnOngedierte],
  'ratten': [TicketLabel.AfvalEnOngedierte],
  'muizen': [TicketLabel.AfvalEnOngedierte],
  'kakkerlakken': [TicketLabel.AfvalEnOngedierte],
  'afval': [TicketLabel.AfvalEnOngedierte],

  // Huurdersdiscussies - tenant discussions
  'huurincasso': [TicketLabel.Huurdersdiscussies],
  'huurdersdiscussies': [TicketLabel.Huurdersdiscussies],
  'huurders': [TicketLabel.Huurdersdiscussies],
  'klacht': [TicketLabel.Huurdersdiscussies],

  // HVAC
  'hvac': [TicketLabel.HVAC],
  'airco': [TicketLabel.HVAC],
  'klimaat': [TicketLabel.HVAC],

  // Afbraakwerken - demolition work
  'afbraak': [TicketLabel.Afbraakwerken],
  'afbreken': [TicketLabel.Afbraakwerken],
  'slopen': [TicketLabel.Afbraakwerken],
  'demolitie': [TicketLabel.Afbraakwerken],
  'verwijderen': [TicketLabel.Afbraakwerken],

  // Asbest
  'asbest': [TicketLabel.Asbest],
  'asbesthoudend': [TicketLabel.Asbest],
  'asbestverwijdering': [TicketLabel.Asbest],

  // Balustrade - railings
  'balustrade': [TicketLabel.Balustrade],
  'leuning': [TicketLabel.Balustrade],
  'reling': [TicketLabel.Balustrade],
  'hekwerk': [TicketLabel.Balustrade],

  // Brandbeveiliging - fire safety
  'brand': [TicketLabel.Brandbeveiliging],
  'brandmelder': [TicketLabel.Brandbeveiliging],
  'brandveiligheid': [TicketLabel.Brandbeveiliging],
  'sprinkler': [TicketLabel.Brandbeveiliging],
  'brandblusser': [TicketLabel.Brandbeveiliging],
  'rookmelder': [TicketLabel.Brandbeveiliging],

  // Elastische Voegen - elastic joints
  'voeg': [TicketLabel.ElastischeVoegen],
  'voegen': [TicketLabel.ElastischeVoegen],
  'kit': [TicketLabel.ElastischeVoegen],
  'kitten': [TicketLabel.ElastischeVoegen],
  'afdichting': [TicketLabel.ElastischeVoegen],

  // Electronische toestellen - electronic appliances
  'toestel': [TicketLabel.ElectronischeToestellen],
  'apparaat': [TicketLabel.ElectronischeToestellen],
  'elektronica': [TicketLabel.ElectronischeToestellen],
  'wasmachine': [TicketLabel.ElectronischeToestellen],
  'droogkast': [TicketLabel.ElectronischeToestellen],
  'oven': [TicketLabel.ElectronischeToestellen],
  'vaatwasser': [TicketLabel.ElectronischeToestellen],

  // Nutsvoorzieningen - utilities
  'nuts': [TicketLabel.Nutsvoorzieningen],
  'nutsvoorziening': [TicketLabel.Nutsvoorzieningen],
  'aansluiting': [TicketLabel.Nutsvoorzieningen],
  'energieaansluiting': [TicketLabel.Nutsvoorzieningen],

  // Boekhouding - accounting
  'boekhouding': [TicketLabel.Boekhouding],
  'administratie': [TicketLabel.Boekhouding],
  'financieel': [TicketLabel.Boekhouding],
  'boekhoudkundig': [TicketLabel.Boekhouding],

  // Facturatie - invoicing
  'factuur': [TicketLabel.Facturatie],
  'facturatie': [TicketLabel.Facturatie],
  'rekening': [TicketLabel.Facturatie],
  'betaling': [TicketLabel.Facturatie],

  // Gebouwattributen - building attributes
  'gebouwkenmerk': [TicketLabel.Gebouwattributen],
  'attribuut': [TicketLabel.Gebouwattributen],
  'eigenschap': [TicketLabel.Gebouwattributen],
  'specificatie': [TicketLabel.Gebouwattributen],

  // Gevel - facade
  'gevel': [TicketLabel.Gevel],
  'buitenmuur': [TicketLabel.Gevel],
  'voorgevel': [TicketLabel.Gevel],
  'zijgevel': [TicketLabel.Gevel],

  // Inrichting - interior/furnishing
  'inrichting': [TicketLabel.Inrichting],
  'interieur': [TicketLabel.Inrichting],
  'decoratie': [TicketLabel.Inrichting],

  // Internet
  'internet': [TicketLabel.Internet],
  'wifi': [TicketLabel.Internet],
  'netwerk': [TicketLabel.Internet],
  'verbinding': [TicketLabel.Internet],
  'connectivity': [TicketLabel.Internet],

  // Karweien - odd jobs
  'karwei': [TicketLabel.Karweien],
  'karweien': [TicketLabel.Karweien],
  'klusje': [TicketLabel.Karweien],
  'klusjes': [TicketLabel.Karweien],
  'klein onderhoud': [TicketLabel.Karweien],

  // Overige - miscellaneous
  'overig': [TicketLabel.Overige],
  'overige': [TicketLabel.Overige],
  'anders': [TicketLabel.Overige],
  'diversen': [TicketLabel.Overige],

  // Rolluik - roller shutter
  'rolluik': [TicketLabel.Rolluik],
  'zonnescherm': [TicketLabel.Rolluik],
  'screen': [TicketLabel.Rolluik],
  'screens': [TicketLabel.Rolluik],

  // Ruwbouw - structural construction
  'ruwbouw': [TicketLabel.Ruwbouw],
  'structuur': [TicketLabel.Ruwbouw],
  'beton': [TicketLabel.Ruwbouw],
  'draagmuur': [TicketLabel.Ruwbouw],

  // Stabiliteit - structural stability
  'stabiliteit': [TicketLabel.Stabiliteit],
  'scheur': [TicketLabel.Stabiliteit],
  'scheuren': [TicketLabel.Stabiliteit],
  'verzakking': [TicketLabel.Stabiliteit],
  'constructie': [TicketLabel.Stabiliteit],

  // Suggesties huurders ikv verbeteringen - tenant improvement suggestions
  'suggestie': [TicketLabel.SuggestiesHuurdersVerbeteringen],
  'voorstel': [TicketLabel.SuggestiesHuurdersVerbeteringen],
  'verbetering': [TicketLabel.SuggestiesHuurdersVerbeteringen],
  'idee': [TicketLabel.SuggestiesHuurdersVerbeteringen],

  // Trap - stairs
  'trap': [TicketLabel.Trap],
  'trappen': [TicketLabel.Trap],
  'traphal': [TicketLabel.Trap],
  'trappenhuis': [TicketLabel.Trap],

  // Vergunning/attest - permit/certificate
  'vergunning': [TicketLabel.VergunningAttest],
  'attest': [TicketLabel.VergunningAttest],
  'certificaat': [TicketLabel.VergunningAttest],
  'conformiteit': [TicketLabel.VergunningAttest],

  // Vloeren en parket - floors and parquet
  'vloer': [TicketLabel.VloerenParket],
  'vloeren': [TicketLabel.VloerenParket],
  'parket': [TicketLabel.VloerenParket],
  'laminaat': [TicketLabel.VloerenParket],
  'tegels': [TicketLabel.VloerenParket],
  'plavuizen': [TicketLabel.VloerenParket],

  // Vragen mbt huurcontract - rental contract questions
  'huurcontract': [TicketLabel.VragenHuurcontract],
  'contract': [TicketLabel.VragenHuurcontract],
  'huurovereenkomst': [TicketLabel.VragenHuurcontract],
  'lease': [TicketLabel.VragenHuurcontract],

  // Tuinonderhoud - garden maintenance
  'tuin': [TicketLabel.Tuinonderhoud],
  'tuinonderhoud': [TicketLabel.Tuinonderhoud],
  'groen': [TicketLabel.Tuinonderhoud],
  'gras': [TicketLabel.Tuinonderhoud],
  'planten': [TicketLabel.Tuinonderhoud],
  'beplanting': [TicketLabel.Tuinonderhoud],

  // Signalisatie - signage
  'signalisatie': [TicketLabel.Signalisatie],
  'bord': [TicketLabel.Signalisatie],
  'bewegwijzering': [TicketLabel.Signalisatie],
  'pictogram': [TicketLabel.Signalisatie],

  // Meubilair - furniture
  'meubilair': [TicketLabel.Meubilair],
  'meubel': [TicketLabel.Meubilair],
  'meubelen': [TicketLabel.Meubilair],
  'kast': [TicketLabel.Meubilair],
  'tafel': [TicketLabel.Meubilair],
  'stoel': [TicketLabel.Meubilair],

  // Bezetting - occupancy/squatting
  'bezetting': [TicketLabel.Bezetting],
  'kraak': [TicketLabel.Bezetting],
  'illegaal': [TicketLabel.Bezetting],
  'ongeoorloofd': [TicketLabel.Bezetting],
};

/**
 * Extract building ID van verschillende formats
 * Voorbeelden:
 * - "2000-EMIE-12" → "2000-EMIE-12"
 * - "EMIE-12" → "2000-EMIE-12" (voeg jaartal toe)
 * - "Emiel Banningstraat 12" → null (moet via fuzzy search)
 */
export function extractBuildingId(text: string): string | null {
  // Volledige format: YYYY-CODE-XX
  const fullMatch = text.match(/\b(\d{4}-[A-Z]+-\d{2})\b/i);
  if (fullMatch) {
    return fullMatch[1].toUpperCase();
  }

  // Gedeeltelijke format: CODE-XX (voeg 2000 toe als default)
  const partialMatch = text.match(/\b([A-Z]+-\d{2})\b/i);
  if (partialMatch) {
    return `2000-${partialMatch[1].toUpperCase()}`;
  }

  return null;
}

/**
 * Extract unit ID
 */
function extractUnitId(text: string): string | null {
  const unitMatch = text.match(/unit\s+(\w+)/i) || text.match(/box\s+(\w+)/i);
  return unitMatch ? unitMatch[1] : null;
}

/**
 * Extract label van tekst with word boundary checking
 * Also handles "ikv" (in kader van) and "mbt" (met betrekking tot) context
 */
function extractLabel(text: string): string | null {
  const normalized = text.toLowerCase();

  // Handle "ikv" (in kader van) or "mbt" (met betrekking tot) - extract the word after it
  const contextMatch = normalized.match(/(?:ikv|mbt)\s+(\w+)/);
  if (contextMatch) {
    const contextWord = contextMatch[1];
    console.log(`🔍 Found "ikv/mbt" context: "${contextWord}"`);
    // Try to match the context word against label keywords
    for (const [keyword, labels] of Object.entries(labelKeywords)) {
      if (contextWord.includes(keyword) || keyword.includes(contextWord)) {
        console.log(`🏷️ Label detected via ikv/mbt: "${contextWord}" → ${labels[0]}`);
        return labels[0];
      }
    }
  }

  // Sort keywords by length (longest first) to match more specific terms first
  const sortedKeywords = Object.entries(labelKeywords).sort((a, b) => b[0].length - a[0].length);

  for (const [keyword, labels] of sortedKeywords) {
    // Use word boundaries to avoid partial matches
    // For multi-word keywords, check for exact phrase
    const regex = new RegExp(`\\b${keyword.replace(/\s+/g, '\\s+')}\\b`, 'i');

    if (regex.test(normalized)) {
      console.log(`🏷️ Label detected: "${keyword}" → ${labels[0]}`);
      return labels[0]; // Return first matching label
    }
  }

  console.log('⚠️ No label detected in query - showing all labels');
  return null;
}

/**
 * Bepaal query type op basis van keywords en detected label
 * @param text - The user's query text
 * @param detectedLabel - The label extracted from the query (null if none detected)
 */
function determineQueryType(text: string, detectedLabel: string | null): AnalyticsQuery['type'] {
  const normalized = text.toLowerCase();

  // PRIORITY 1: List queries (geef mij alle, toon alle, toon tickets, welke) → filtered-list
  const listKeywords = ['geef mij alle', 'geef alle', 'toon alle', 'laat alle zien', 'lijst van', 'toon tickets', 'toon ticket'];
  const hasListKeyword = listKeywords.some(kw => normalized.includes(kw));

  if (hasListKeyword) {
    // List queries always show filtered-list
    // Example: "geef mij alle tickets ikv huurdersdiscussies" → filtered-list
    // Example: "toon tickets van deze week" → filtered-list
    console.log(`📋 Query type: filtered-list (list keyword detected)`);
    return 'filtered-list';
  }

  // "welke" queries with a detected label → filtered-list
  // Example: "welke vragen mbt huurdersdiscussies staan open" → filtered-list
  if (normalized.includes('welke') && detectedLabel) {
    console.log(`📋 Query type: filtered-list ("welke" + label detected)`);
    return 'filtered-list';
  }

  // PRIORITY 2: Count/frequency keywords with specific label → filtered-list
  // This MUST be checked before other analytics types to catch queries like "hoeveel elektriciteits tickets"
  const countKeywords = ['hoeveel', 'aantal', 'frequentie', 'meeste'];
  const hasCountKeyword = countKeywords.some(kw => normalized.includes(kw));

  if (hasCountKeyword && detectedLabel) {
    // If a specific label was detected → show filtered list of those tickets
    // Example: "hoeveel tickets ikv elektriciteit" → filtered-list (show Elektriciteit tickets)
    console.log(`📋 Query type: filtered-list (label: ${detectedLabel} detected with count keyword)`);
    return 'filtered-list';
  }

  // PRIORITY 3: Reporter distribution keywords
  if (
    normalized.includes('welke reporter') ||
    normalized.includes('welke melder') ||
    normalized.includes('wie meldt') ||
    normalized.includes('meeste tickets van') ||
    normalized.includes('per reporter') ||
    normalized.includes('per melder') ||
    normalized.includes('reporter geeft')
  ) {
    console.log('👤 Query type: by-reporter (reporter distribution detected)');
    return 'by-reporter';
  }

  // PRIORITY 4: Time evolution keywords
  if (
    normalized.includes('evolutie') ||
    normalized.includes('evolueren') ||
    normalized.includes('evolueerde') ||
    normalized.includes('evolueerden') ||
    normalized.includes('geëvolueerd') ||
    normalized.includes('over tijd') ||
    normalized.includes('over de tijd') ||
    normalized.includes('doorheen de tijd') ||
    normalized.includes('trend') ||
    normalized.includes('per week') ||
    normalized.includes('per maand')
  ) {
    return 'time-series';
  }

  // PRIORITY 5: Distribution keywords (gebouw/unit)
  // Check for explicit "per gebouw" or "welk gebouw"
  if (
    normalized.includes('per gebouw') ||
    normalized.includes('welk gebouw') ||
    normalized.includes('welke gebouwen')
  ) {
    return 'by-building';
  }

  // "waar" only triggers by-building if NO specific label was detected
  // Example: "waar heb ik de meeste problemen" (no label) → by-building
  // Example: "waar heb ik elektriciteitsproblemen" (has label) → filtered-list
  if (normalized.includes('waar') && !detectedLabel) {
    return 'by-building';
  }

  // Unit verdeling keywords
  if (
    normalized.includes('per unit') ||
    normalized.includes('welke unit') ||
    normalized.includes('welke units')
  ) {
    return 'by-unit';
  }

  // PRIORITY 6: Count keywords without specific label → label-frequency
  if (hasCountKeyword) {
    // If NO label detected → show label distribution
    // Example: "hoeveel tickets heb ik" → label-frequency (show all label counts)
    console.log('📊 Query type: label-frequency (count keyword without specific label)');
    return 'label-frequency';
  }

  // PRIORITY 7: "verdeling" keyword → label-frequency
  if (normalized.includes('verdeling')) {
    return 'label-frequency';
  }

  return 'unknown';
}

/**
 * Detect if user wants to include closed tickets
 * Default: false (only show open tickets)
 */
function shouldIncludeClosedTickets(text: string): boolean {
  const normalized = text.toLowerCase();

  // Keywords that indicate user wants closed tickets included
  const includeClosedKeywords = [
    'inclusief gesloten',
    'incl gesloten',
    'met gesloten',
    'inclusief afgehandeld',
    'alle tickets',
    'alle status',
  ];

  // Keywords that indicate user explicitly wants only open tickets
  const onlyOpenKeywords = [
    'alleen open',
    'enkel open',
    'open tickets',
    'actieve tickets',
    'niet gesloten',
    'zonder gesloten',
    'open staan',
    'momenteel open',
    'die open staan',
    'nog open',
  ];

  // Check for explicit inclusion of closed
  if (includeClosedKeywords.some(keyword => normalized.includes(keyword))) {
    console.log('✓ Including closed tickets (user requested)');
    return true;
  }

  // Check for explicit exclusion
  if (onlyOpenKeywords.some(keyword => normalized.includes(keyword))) {
    console.log('✓ Excluding closed tickets (user requested)');
    return false;
  }

  // Keywords that suggest user is asking about current problems (exclude closed)
  const problemKeywords = [
    'probleem',
    'problemen',
    'waar heb ik',
    'welke',
    'hoeveel heb ik',
  ];

  if (problemKeywords.some(keyword => normalized.includes(keyword))) {
    console.log('✓ Excluding closed tickets (default for problem queries)');
    return false;
  }

  // Default: exclude closed tickets
  console.log('✓ Excluding closed tickets (default)');
  return false;
}

/**
 * Extract datum filters
 */
function extractDateFilters(text: string): {
  startDate?: Date;
  endDate?: Date;
  granularity?: 'day' | 'week' | 'month';
} {
  const normalized = text.toLowerCase();
  const now = new Date();
  const result: { startDate?: Date; endDate?: Date; granularity?: 'day' | 'week' | 'month' } = {};

  // Granularity
  if (normalized.includes('per dag')) {
    result.granularity = 'day';
  } else if (normalized.includes('per week')) {
    result.granularity = 'week';
  } else if (normalized.includes('per maand')) {
    result.granularity = 'month';
  }

  // Relatieve datums
  if (normalized.includes('vandaag')) {
    result.startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    result.endDate = now;
  } else if (normalized.includes('deze week')) {
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
    result.startDate = startOfWeek;
    result.endDate = now;
  } else if (normalized.includes('deze maand')) {
    result.startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    result.endDate = now;
  } else if (normalized.includes('dit jaar')) {
    result.startDate = new Date(now.getFullYear(), 0, 1);
    result.endDate = now;
  } else if (normalized.includes('laatste 7 dagen')) {
    result.startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    result.endDate = now;
  } else if (normalized.includes('laatste 30 dagen')) {
    result.startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    result.endDate = now;
  } else if (normalized.includes('laatste 3 maanden')) {
    result.startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
    result.endDate = now;
  }

  // Als geen specifieke datum, default naar laatste 3 maanden voor time-series
  if (!result.startDate && !result.endDate) {
    result.startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1);
    result.endDate = now;
  }

  return result;
}

/**
 * Genereer beschrijvende titel voor de query
 */
function generateTitle(query: AnalyticsQuery): string {
  const { type, filters } = query;

  let title = '';

  // Basis type
  switch (type) {
    case 'time-series':
      title = 'Evolutie van tickets over tijd';
      break;
    case 'by-building':
      title = 'Tickets per gebouw';
      break;
    case 'by-unit':
      title = 'Tickets per unit';
      break;
    case 'by-reporter':
      title = 'Tickets per reporter';
      break;
    case 'label-frequency':
      title = 'Ticket verdeling per categorie';
      break;
    case 'filtered-list':
      // For filtered lists, create a more specific title
      if (filters.label) {
        title = `${filters.label} tickets`;
      } else {
        title = 'Tickets';
      }
      break;
    default:
      title = 'Ticket statistieken';
  }

  // Voeg filters toe aan titel (skip label for filtered-list as it's already in the title)
  if (filters.label && type !== 'filtered-list') {
    title += ` - ${filters.label}`;
  }
  if (filters.buildingId) {
    title += ` - ${filters.buildingId}`;
  }
  if (filters.unitId) {
    title += ` - Unit ${filters.unitId}`;
  }

  return title;
}

/**
 * Parse natuurlijke taal query naar gestructureerde analytics query
 */
export function parseAnalyticsQuery(text: string): AnalyticsQuery {
  const buildingId = extractBuildingId(text);
  const unitId = extractUnitId(text);
  const label = extractLabel(text);
  const type = determineQueryType(text, label); // Pass detected label to determine correct type
  const dateFilters = extractDateFilters(text);
  const includeClosedTickets = shouldIncludeClosedTickets(text);

  const query: AnalyticsQuery = {
    type,
    filters: {
      buildingId: buildingId || undefined,
      unitId: unitId || undefined,
      label: label || undefined,
      includeClosedTickets,
      ...dateFilters,
    },
    granularity: dateFilters.granularity,
    title: '',
  };

  // Bepaal chart type op basis van query type
  if (type === 'time-series') {
    query.chartType = 'line';
  } else if (type === 'filtered-list') {
    query.chartType = 'bar'; // Show filtered list as bar chart (tickets per building) + table below
  } else {
    query.chartType = 'bar'; // Aggregation views as bar chart
  }

  query.title = generateTitle(query);

  return query;
}

/**
 * Voorbeeldqueries voor gebruikers
 */
export const EXAMPLE_QUERIES = [
  'Hoeveel tickets ivm vochtschade heb ik?',
  'Welke reporter geeft de meeste tickets?',
  'Waar heb ik de meeste elektriciteitsproblemen?',
  'Hoe evolueren tickets voor 2000-EMIE-12 over tijd?',
  'Tickets per gebouw deze maand',
  'Evolutie van verwarmingsproblemen',
  'Hoeveel ramen tickets in EMIE-12?',
  'Hoeveel brandmelders zijn defect?',
  'Waar heb ik de meeste liftstoringen?',
  'Evolutie van internetproblemen dit jaar',
  'Hoeveel tuinonderhoud tickets per gebouw?',
];
